import React, { useState, useEffect } from 'react';
import { action, computed } from 'mobx';
import { observer } from 'mobx-react';
import { useViewerControl } from 'libs/rendering/gltf/hooks';
import { useClientStore } from 'apps/hooks';

import { isMobile } from 'styles/utils';
import { UiModes, UiTimerDelays } from 'libs/rendering/gltf/config';
import Icon from 'components/Icon';
import Paragraph from 'components/Paragraph';
import Tooltip from 'components/Tooltip';
import { themes } from 'styles/themes/wikifactoryNew';

import {
  NodeListStyled,
  NodeStyled,
  IconStyled,
  ActionsDesktopStyled,
  ActionsMobileStyled,
  ButtonStyled,
  ActionsBlock,
  RowWrapper,
  RowContentWrapper,
  NameWrapper,
  TreeLinesBlock,
  TreeLineStyled,
} from './tree.styled';

export const getInstancePublicName = instance => {
  if (!instance) return null;

  let out = instance?.name;

  const part = instance?.GetPart();
  let partName = null;
  if (part && part.name.length > 0) {
    partName = part.name;
  }

  if (out.length === 0 || partName?.length > out.length) {
    // use part name
    out = partName;

    // check body name...
    const body = instance?.GetBody();
    if (body && body.name.length > 0) {
      if (out.length === 0) {
        out = body.name;
      }
    }
  }

  if (out?.length === 0) {
    if (instance?.isDrawing) {
      out = '_UNNAMED_';
    } else if (instance?.isSubassembly) {
      out = '_UNNAMED_';
    } else {
      out = '_UNNAMED_';
    }
  }

  if (out.endsWith(':1')) {
    out = out.substring(0, out.length - 2);
  }

  return out;
};

class NodeState {
  constructor(assemblyState, cadItem, viewerControl) {
    this.viewerControl = viewerControl;

    this.assemblyState = assemblyState;
    this.cadItem = cadItem;

    this.selManager = viewerControl?.viewer?.selectionManager;
    this.settings = viewerControl?.viewer?.settings;
    this.engine = viewerControl?.viewer?.engine;
    this.module = viewerControl?.viewer?.module;

    this.rowHovered = false;
  }

  // auto-scroll...
  @computed get nodeRef() {
    return React.createRef();
  }

  // mobile kebab menu
  @action SetKebabExpanded = item => {
    this.assemblyState.kebabNodeId = item ? item.uuid : null;
  };

  @computed get kebebExpanded() {
    return this.cadItem.uuid === this.assemblyState.kebabNodeId;
  }

  // cad item (Product/Instance)
  @computed get publicName() {
    let out = this.cadItem?.name;

    if (this.cadItem?.IsAssemblyInstance()) {
      out = getInstancePublicName(this.cadItem);
    } else if (out?.length === 0) {
      out = '_UNNAMED_';
    }

    if (out.endsWith(':1')) {
      out = out.substring(0, out.length - 2);
    }

    return out;
  }

  @computed get visible() {
    return this.cadItem?.visible;
  }

  @computed get isExpanded() {
    return this.cadItem?.IsAssemblyProduct() && this.cadItem.expanded;
  }

  @computed get isInspected() {
    return this.cadItem?.inspected;
  }

  @computed get isSelected() {
    if (this.cadItem) {
      if (this.cadItem.selected) return true;

      if (this.isInstance) {
        if (this.selManager?.GetSelectionCount() > 0) {
          const tgtSel = this.cadItem?.GetSelfTargetItem();
          if (tgtSel) {
            const selIx = this.selManager?.ContainsSelectionItemTarget(
              tgtSel._wasm,
            );
            return selIx >= 0;
          }
        }
      }
    }
    return false;
  }

  @computed get isRoot() {
    if (!this.isInstance && this.cadItem?.GetParent() == null) return true;
    return false;
  }

  @computed get isCurrentRoot() {
    if (this.isRoot) return true;

    const prod = this.settings.CurrentCamera()?.GetSubAssembly();
    if (prod && prod?.uuid === this.cadItem.uuid) {
      return true;
    }
    return false;
  }

  @computed get hidesHighlightedInstance() {
    if (this.cadItem == null) return false;
    if (this.selManager == null) return false;
    if (this.selManager.HighlightCount() === 0) return false;
    if (!this.isSubassembly) return false;
    if (this.cadItem.expanded) return false;
    if (this.isCurrentRoot) return false;

    return this.selManager.HasInnerInstanceHighlighted(this.cadItem?._wasm);
  }

  @computed get isHighlighted() {
    if (this.isDrawing || this.isSubassembly) {
      return this.cadItem.highlighted;
    }

    if (this.isInstance) {
      if (this.selManager?.HighlightCount() > 0) {
        const tgtSel = this.cadItem.GetSelfTargetItem();
        if (tgtSel) {
          const highIx = this.selManager?.ContainsHighlightItemTarget(
            tgtSel._wasm,
          );
          if (highIx >= 0) {
            // highlighted, but how?
            const fromParentNode =
              this.selManager?.GetHighlight(highIx)?.source ===
              this.module.SelectionSource.AdvancedGuiAux;
            return !fromParentNode;
          }
        }
      }
    }
    return false;
  }

  @computed get isProduct() {
    return this.cadItem?.IsAssemblyProduct();
  }

  @computed get isSubassembly() {
    return this.cadItem?.IsAssemblyProduct() && this.cadItem?.IsSubassembly();
  }

  @computed get isInstance() {
    return this.cadItem?.IsAssemblyInstance();
  }

  @computed get hasBrep() {
    return this.isInstance && this.cadItem?.GetBody()?.GetBrep();
  }

  @computed get hasMesh() {
    return this.isInstance && !this.cadItem?.GetBody()?.GetBrep();
  }

  @computed get isDrawing() {
    return this.cadItem?.IsDrawing();
  }

  // drawing
  @computed get drawings() {
    if (this.cadItem?.IsAssemblyProduct()) {
      const part = this.cadItem?.GetPart();
      if (part) {
        if (part.drawings.size() > 0) {
          return part.drawings;
        }
      }
    }
    return null;
  }

  @action toggleExpanded = e => {
    if (this.cadItem?.IsAssemblyProduct()) {
      if (!this.cadItem?.isCurrentRoot) {
        this.cadItem.expanded = !this.cadItem.expanded;
      }
    }

    if (e) e.stopPropagation();
  };

  @action toggleNodeVisibility = () => {
    const canToggle = this.cadItem?.IsAssemblyInstance() || this.isSubassembly;

    if (canToggle) {
      this.cadItem.visible = !this.cadItem.visible;

      if (this.isSubassembly) {
        // propagate down the tree...
        const instances = this.cadItem?.GetInstancesBelow().keys();
        for (let i = 0; i < instances.size(); i++) {
          const instance = instances.get(i);
          instance.visible = this.cadItem.visible;
        }

        const products = this.cadItem?.GetProductsBelow();
        // eslint-disable-next-line no-restricted-syntax
        for (const product of products) {
          product.visible = this.cadItem.visible;
        }
      } else {
        // propagate to parent (if all siblings changed)
        const parent = this.cadItem?.GetParentProduct().GetParent();
        if (parent) {
          const instances = parent.GetInstancesBelow().keys();
          let mustPropagateUp = true;
          for (let i = 0; i < instances.size(); i++) {
            const instance = instances.get(i);
            if (instance.uuid !== this.cadItem.uuid) {
              if (instance.GetBody() != null) {
                // take into account only instances with body
                if (instance.visible !== this.cadItem.visible) {
                  mustPropagateUp = false;
                  break;
                }
              }
            }
          }
          if (mustPropagateUp) {
            parent.visible = !parent.visible;
          }
        }
      }

      // eslint-disable-next-line no-unused-expressions
      this.viewerControl?.ForceRender();
    }
  };

  // HIGHLIGHT
  @action setHighlightBehaviour = mode => {
    if (this.viewerControl) {
      if (mode == null) {
        this.viewerControl.SetHighlightBehaviour(null, null);
      } else {
        this.viewerControl.SetHighlightBehaviour(mode, null);
      }
    }
  };

  @action highlightNode = () => {
    if (this.isCurrentRoot) return;

    if (this.isDrawing) {
      this.cadItem.highlighted = true;
      return;
    }

    if (this.selManager) {
      // clear current highlight...
      this.selManager.ClearHighlight();

      if (this.cadItem?.IsAssemblyInstance()) {
        const selfTarget = this.cadItem.GetSelfTargetItem();
        if (selfTarget && !this.isHighlighted) {
          // tell selection is from UI instance tree node...
          selfTarget.source = this.module.SelectionSource.AdvancedGui;
          this.selManager.AddHighlight(selfTarget._wasm);
        }
      } else {
        // highlight subassembly (all instances below).
        // use timer to overcome onMouseLeave bug:
        // https://github.com/facebook/react/issues/6807
        // special behavior...
        this.selManager.AddHighlightItem(this.cadItem._wasm);
        this.cadItem.highlighted = true;
        if (this.isInstance) {
          // tell selection is from UI instance tree node...
          const lastHigh = this.selManager.GetLastHighlight();
          if (lastHigh)
            lastHigh.source = this.module.SelectionSource.AdvancedGui;
        }
      }
    }
  };

  @action clearHighlight = () => {
    if (this.viewerControl && this.selManager && this.cadItem) {
      this.selManager.ClearHighlight();
      this.cadItem.highlighted = false;
      // enure high mode backs to default
      // (sometimes onMouseLeave is not called:
      // https://github.com/facebook/react/issues/6807)
      this.setHighlightBehaviour();
    }
  };

  // SELECT
  @action selectNode = () => {
    if (!this.cadItem?.IsAssemblyInstance()) return;

    if (this.selManager) {
      const selfTarget = this.cadItem?.GetSelfTargetItem();
      if (this.cadItem?.IsAssemblyInstance()) {
        if (selfTarget && !this.isSelected) {
          this.selManager.AddSelection(selfTarget._wasm);
        } else {
          const selIx = this.selManager.ContainsSelectionItemTarget(
            selfTarget._wasm,
          );
          this.selManager.DeleteSelection(selIx);
        }
      } else {
        const selIx = this.selManager.ContainsSelectionItemTarget(
          selfTarget._wasm,
        );
        this.selManager.DeleteSelection(selIx);
      }
    }
  };

  // INSPECT (new TAB)
  @action inspectNode = () => {
    this.viewerControl.InspectCadItem(this.cadItem);
    this.cadItem.highlighted = false;
    if (isMobile()) {
      this.viewerControl.UpdateUiMode(UiModes.collapsed);
    }
  };
}

const AssemblyNode = observer(function _AssemblyNode(props) {
  const viewerControl = useViewerControl();
  const clientStore = useClientStore();

  const { level, cadItem, assemblyState } = props;

  const [nodeState] = useState(
    () => new NodeState(assemblyState, cadItem, viewerControl),
  );

  const subTree = () => {
    const items = [];
    if (nodeState.cadItem && nodeState.cadItem.IsAssemblyProduct()) {
      const childCount = nodeState.cadItem.ChildCount();
      if (childCount > 0) {
        for (let i = 0; i < childCount; i++) {
          const child = nodeState.cadItem.GetChild(i);
          if (child) {
            const innerChildCount = child.ChildCount();
            if (innerChildCount === 0) {
              const instance = child.GetInstance();
              if (instance && instance.GetBody()) {
                items.push(
                  <AssemblyNode
                    key={instance.uuid}
                    cadItem={instance}
                    level={level + 1}
                    assemblyState={assemblyState}
                  />,
                );
              }
            } else if (innerChildCount === 1) {
              // 'superfluous' wrapper product, get its instance
              let innerChild = child.GetChild(0);
              if (innerChild) {
                let instance = null;
                do {
                  instance = innerChild.GetInstance();
                  if (instance === null) {
                    // nested "superfluous" wrapper product
                    innerChild = innerChild.GetChild(0);
                  }
                } while (instance === null && innerChild != null);

                if (instance && instance.GetBody()) {
                  items.push(
                    <AssemblyNode
                      key={instance.uuid}
                      cadItem={instance}
                      level={level + 1}
                      assemblyState={assemblyState}
                    />,
                  );
                }
              }
            } else {
              items.push(
                <AssemblyNode
                  key={child.uuid}
                  cadItem={child}
                  level={level + 1}
                  assemblyState={assemblyState}
                />,
              );
            }
          }
        }
      }

      // drawings
      if (nodeState.isProduct) {
        const part = nodeState.cadItem.GetPart();
        if (part) {
          const { drawings } = part;
          if (drawings.size() > 0) {
            for (let i = 0; i < drawings.size(); i++) {
              const drawing = part.GetDrawing(i);
              if (drawing) {
                items.push(
                  <AssemblyNode
                    key={childCount + i}
                    cadItem={drawing}
                    level={level + 1}
                    assemblyState={assemblyState}
                  />,
                );
              }
            }
          }
        }
      }
    }
    return items;
  };

  const instanceActions = () => {
    const assy = viewerControl.cadFile.assembly;
    const isPlaneXY = assy?.IsPlaneXY();
    if (isPlaneXY !== false) return [];

    const actions = [];
    if (nodeState.isInstance) {
      const body = nodeState.cadItem.GetBody();
      const mesh = body?.GetBodyMesh();

      if (nodeState.isHighlighted) {
        actions.push({
          key: 1,
          icon: nodeState.isInspected ? 'search' : 'expand', // new tab
          onMouseEnter: () => nodeState.setHighlightBehaviour('on-front'),
          onMouseLeave: () => nodeState.setHighlightBehaviour(),
          onClick: nodeState.inspectNode,
          tooltip: nodeState.isInspected ? 'View Tab' : 'New Tab',
        });
      }

      if (nodeState.isHighlighted) {
        if (mesh != null && mesh.IsFacetedGeometry()) {
          // actions render on-front when hovered...
          actions.push({
            key: 0,
            icon: nodeState.visible ? 'view_off' : 'view', // visibility
            onClick: nodeState.toggleNodeVisibility,
            tooltip: nodeState.visible ? 'Hide' : 'Show',
          });
        }
      }
    }
    return actions;
  };

  const subassemblyActions = () => {
    const assy = viewerControl.cadFile.assembly;
    const isPlaneXY = assy?.IsPlaneXY();
    if (isPlaneXY !== false) return [];

    const actions = [];
    if (nodeState.isSubassembly && nodeState.isHighlighted) {
      if (nodeState.isInspected || nodeState.isHighlighted) {
        actions.push({
          key: 1,
          icon: nodeState.isInspected ? 'search' : 'expand', // new tab
          onMouseEnter: () => nodeState.setHighlightBehaviour('on-front'),
          onMouseLeave: () => nodeState.setHighlightBehaviour(),
          onClick: nodeState.inspectNode,
          tooltip: nodeState.isInspected ? 'View Tab' : 'New Tab',
        });
      }

      if (nodeState.isHighlighted) {
        // actions render on-front when hovered...
        actions.push({
          key: 0,
          icon: nodeState.visible ? 'view_off' : 'view', // visibility
          onClick: nodeState.toggleNodeVisibility,
          tooltip: nodeState.visible ? 'Hide' : 'Show',
        });
      }
    }

    return actions;
  };

  const drawingActions = () => {
    let actions = [];
    if (nodeState.isDrawing) {
      if (nodeState.isInspected || nodeState.isHighlighted) {
        actions = [
          {
            key: 0,
            icon: nodeState.isInspected ? 'search' : 'expand', // new tab
            onClick: nodeState.inspectNode,
            tooltip: nodeState.isInspected ? 'View Tab' : 'New Tab',
          },
        ];
      }
    }

    return actions;
  };

  const handleNodeClick = () => {
    if (nodeState.isProduct) {
      nodeState.toggleExpanded();
    } else if (nodeState.isInstance) {
      if (clientStore.isMobile) {
        nodeState.inspectNode();
        viewerControl.UpdateUiMode(UiModes.collapsed);
      } else {
        nodeState.selectNode();
      }
    }
  };

  // tree lines
  const treeLines = Array.from(Array(level).keys());
  const TreeLine = ix => {
    return ix === level - 1 && !nodeState.isProduct ? (
      <TreeLineStyled key={`treeLine_${ix}`} hasTick />
    ) : (
      <TreeLineStyled key={`treeLine_${ix}`} />
    );
  };

  // auto-scroll to highlighted
  useEffect(() => {
    if (nodeState.isHighlighted || nodeState.hidesHighlightedInstance) {
      // clear current highlight...
      clearTimeout(viewerControl.scrollTimer);
      viewerControl.SetScrollTimer(null);
      const { current } = assemblyState.nodeRef;
      if (current) {
        if (current.scrollHeight > current.clientHeight) {
          const nodeRef = nodeState.nodeRef.current;
          if (nodeRef) {
            const alreadyVisible =
              nodeRef.offsetTop >= current.scrollTop &&
              nodeRef.offsetTop <= current.scrollTop + current.clientHeight;
            if (!alreadyVisible) {
              const newTimer = setTimeout(() => {
                current.scrollTo(0, nodeRef.offsetTop);
              }, UiTimerDelays.large);
              viewerControl.SetScrollTimer(newTimer);
            }
          }
        }
      }
    }
  }, [nodeState.isHighlighted, nodeState.hidesHighlightedInstance]);

  const shouldShowHighLighted = !isMobile() && nodeState.isHighlighted;

  return (
    <>
      <NodeStyled
        ref={nodeState.nodeRef}
        onClick={handleNodeClick}
        style={{ width: '100%' }}
        data-testid='CADPage-assemblyTreeNodes'
      >
        <Tooltip delayShow={500} place='bottom' id='treeNodeTooltip' />
        <RowWrapper
          isCurrentRoot={nodeState.isCurrentRoot}
          level={level}
          visible={cadItem?.visible}
          highlighted={shouldShowHighLighted}
          selected={nodeState.isSelected}
          isProduct={isMobile() || nodeState.isProduct}
          hidesInstance={nodeState.hidesHighlightedInstance}
          fontWeight='400'
          style={{
            background:
              shouldShowHighLighted || nodeState.isSelected
                ? themes.color.wfGrey100
                : 'transparent',
          }}
        >
          {/* TREE lines */}
          <TreeLinesBlock>{treeLines.map(i => TreeLine(i))}</TreeLinesBlock>

          <RowContentWrapper
            isCurrentRoot={nodeState.isCurrentRoot}
            isProduct={isMobile() || nodeState.isProduct}
            style={{
              width: '100%',
            }}
          >
            {/* icon */}
            <ActionsBlock onClick={e => nodeState.toggleExpanded(e)}>
              {nodeState.isProduct ? (
                nodeState.isCurrentRoot ? (
                  <IconStyled
                    isProduct={true}
                    name={nodeState.isRoot ? 'sub_assembly' : 'assembly'}
                    size='medium'
                  />
                ) : (
                  <IconStyled
                    isProduct={true}
                    name='caret_right'
                    size='small'
                    rotated={nodeState.isExpanded || level === 0}
                  />
                )
              ) : nodeState.isDrawing ? (
                <IconStyled
                  name='drawing'
                  size='small'
                  viewBox='0 0 40 40'
                  isDrawing
                  data-for='treeNodeTooltip'
                  data-tip='drawing'
                  cursor='help'
                />
              ) : nodeState.hasBrep ? (
                <IconStyled
                  name='brep'
                  size='small'
                  viewBox='0 0 40 40'
                  isBody
                  selected={nodeState.isSelected}
                  data-for='treeNodeTooltip'
                  data-tip='brep body'
                  cursor='help'
                />
              ) : (
                <IconStyled
                  name='mesh'
                  size='small'
                  viewBox='0 0 40 40'
                  isBody
                  selected={nodeState.isSelected}
                  data-for='treeNodeTooltip'
                  data-tip='mesh body'
                  cursor='help'
                />
              )}
            </ActionsBlock>

            {/* label */}
            <NameWrapper
              isCurrentRoot={nodeState.isCurrentRoot}
              isProduct={isMobile() || nodeState.isProduct}
              onMouseEnter={nodeState.highlightNode}
              onMouseLeave={nodeState.clearHighlight}
              onDoubleClick={nodeState.inspectNode}
              style={{
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between',
              }}
            >
              <Paragraph className=' hover:text-wfBlue'>
                {nodeState.publicName}
              </Paragraph>

              {/* action buttons */}
              {!isMobile() ? (
                nodeState.isInstance ? (
                  <ActionsDesktopStyled>
                    {instanceActions().map(
                      ({
                        key,
                        icon,
                        tooltip,
                        onMouseEnter,
                        onMouseLeave,
                        onClick,
                      }) => (
                        <span key={key}>
                          <Tooltip
                            delayShow={500}
                            place='bottom'
                            id='treeTooltip'
                          />
                          <ButtonStyled
                            appearance='plain'
                            data-for='treeTooltip'
                            size='small'
                            icon={icon}
                            data-tip={tooltip}
                            selected={nodeState.isSelected}
                            variant='tertiary'
                            onMouseEnter={
                              onMouseEnter
                                ? e => {
                                    e.stopPropagation();
                                    onMouseEnter();
                                  }
                                : null
                            }
                            onMouseLeave={
                              onMouseLeave
                                ? e => {
                                    e.stopPropagation();
                                    onMouseLeave();
                                  }
                                : null
                            }
                            onClick={e => {
                              e.stopPropagation();
                              onClick();
                            }}
                          />
                        </span>
                      ),
                    )}
                  </ActionsDesktopStyled>
                ) : nodeState.isSubassembly ? (
                  <ActionsDesktopStyled>
                    {subassemblyActions().map(
                      ({
                        key,
                        icon,
                        tooltip,
                        onMouseEnter,
                        onMouseLeave,
                        onClick,
                      }) => (
                        <span key={key}>
                          <Tooltip
                            delayShow={500}
                            place='bottom'
                            id='treeTooltip'
                          />
                          <ButtonStyled
                            appearance='plain'
                            data-for='treeTooltip'
                            size='small'
                            icon={icon}
                            data-tip={tooltip}
                            isProduct
                            variant='tertiary'
                            onMouseEnter={
                              onMouseEnter
                                ? e => {
                                    e.stopPropagation();
                                    onMouseEnter();
                                  }
                                : null
                            }
                            onMouseLeave={
                              onMouseLeave
                                ? e => {
                                    e.stopPropagation();
                                    onMouseLeave();
                                  }
                                : null
                            }
                            onClick={e => {
                              e.stopPropagation();
                              onClick();
                            }}
                          />
                        </span>
                      ),
                    )}
                  </ActionsDesktopStyled>
                ) : nodeState.isDrawing ? (
                  <ActionsDesktopStyled>
                    {drawingActions().map(({ key, icon, tooltip, onClick }) => (
                      <span key={key}>
                        <Tooltip
                          delayShow={500}
                          place='bottom'
                          id='treeTooltip'
                        />
                        <ButtonStyled
                          appearance='plain'
                          data-for='treeTooltip'
                          size='small'
                          icon={icon}
                          data-tip={tooltip}
                          variant='tertiary'
                          onClick={e => {
                            e.stopPropagation();
                            onClick();
                          }}
                        />
                      </span>
                    ))}
                  </ActionsDesktopStyled>
                ) : null
              ) : nodeState.isInstance ? (
                <ActionsMobileStyled>
                  {nodeState.kebebExpanded &&
                    instanceActions().map(obj => (
                      <ButtonStyled
                        black
                        appearance='plain'
                        size='small'
                        key={obj.key}
                        selected={nodeState.isSelected}
                        onClick={e => {
                          e.stopPropagation();
                          obj.onClick();
                        }}
                        icon={
                          !nodeState.cadItem?.visible && obj.icon === 'view_off'
                            ? 'view'
                            : obj.icon
                        }
                        variant={
                          nodeState.isSelected || nodeState.isInspected
                            ? 'primary'
                            : 'tertiary'
                        }
                      />
                    ))}
                  {nodeState.kebebExpanded ? (
                    <ButtonStyled
                      grey
                      appearance='plain'
                      size='small'
                      key={0}
                      selected={nodeState.isSelected}
                      onClick={e => {
                        e.stopPropagation();
                        nodeState.SetKebabExpanded(null);
                      }}
                      icon='close'
                      variant={
                        nodeState.isSelected || nodeState.isInspected
                          ? 'primary'
                          : 'tertiary'
                      }
                    />
                  ) : (
                    <ActionsBlock
                      onClick={e => {
                        e.stopPropagation();
                        nodeState.SetKebabExpanded(cadItem);
                      }}
                    >
                      <Icon name='kebab_menu' />
                    </ActionsBlock>
                  )}
                </ActionsMobileStyled>
              ) : nodeState.isDrawing ? (
                <ActionsMobileStyled>
                  {nodeState.kebebExpanded &&
                    drawingActions().map(obj => (
                      <ButtonStyled
                        black
                        appearance='plain'
                        size='small'
                        key={obj.key}
                        selected={nodeState.isSelected}
                        onClick={e => {
                          e.stopPropagation();
                          obj.onClick();
                        }}
                        icon={
                          !nodeState.cadItem?.visible && obj.icon === 'view_off'
                            ? 'view'
                            : obj.icon
                        }
                        variant={
                          nodeState.isSelected || nodeState.isInspected
                            ? 'primary'
                            : 'tertiary'
                        }
                      />
                    ))}
                  {nodeState.kebebExpanded ? (
                    <ButtonStyled
                      grey
                      appearance='plain'
                      size='small'
                      key={0}
                      selected={nodeState.isSelected}
                      onClick={e => {
                        e.stopPropagation();
                        nodeState.SetKebabExpanded(null);
                      }}
                      icon='close'
                      variant={
                        nodeState.isSelected || nodeState.isInspected
                          ? 'primary'
                          : 'tertiary'
                      }
                    />
                  ) : (
                    <ActionsBlock
                      onClick={e => {
                        e.stopPropagation();
                        nodeState.SetKebabExpanded(cadItem);
                      }}
                    >
                      <Icon name='kebab_menu' />
                    </ActionsBlock>
                  )}
                </ActionsMobileStyled>
              ) : null}
            </NameWrapper>
          </RowContentWrapper>
        </RowWrapper>
      </NodeStyled>

      {/* sub-tree (children) */}
      <NodeListStyled>
        {(nodeState.isExpanded || level === 0) && subTree()}
      </NodeListStyled>
    </>
  );
});

export default AssemblyNode;
